import { useCallback, useLayoutEffect, useRef, useState, type RefObject } from 'react';

export const useMeasuredMaxHeight = <TElement extends HTMLElement = HTMLDivElement>(
  isActive: boolean,
  deps: readonly unknown[] = []
): { contentRef: RefObject<TElement | null>; contentHeight: number } => {
  const contentRef = useRef<TElement | null>(null);
  const [contentHeight, setContentHeight] = useState<number>(0);

  const measure = useCallback(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, []);

  useLayoutEffect(() => {
    const element = contentRef.current;
    if (!element) {
      return;
    }
    measure();

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(measure);
      resizeObserver.observe(element);
    } else {
      window.addEventListener('resize', measure);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener('resize', measure);
      }
    };
  }, [isActive, measure, ...deps]);

  return { contentRef, contentHeight } as const;
};
